/**
 * src/domain/identity/webauthn-client.ts
 *
 * Client-Side WebAuthn Passkey Helper.
 * Handles:
 * - Passkey registration (`navigator.credentials.create`) with PRF extension support.
 * - Passkey authentication (`navigator.credentials.get`) with Conditional UI support.
 * - Base64URL conversions for WebAuthn binary buffers.
 */

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export function base64UrlToBuffer(base64url: string): ArrayBuffer {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export interface RegisterPasskeyParams {
  userId: string;
  username: string;
  displayName?: string;
  apiBaseUrl?: string;
}

export interface AuthenticatePasskeyParams {
  userId?: string;
  mediation?: CredentialMediationRequirement; // 'conditional' | 'optional' | 'required'
  apiBaseUrl?: string;
}

export class WebAuthnClient {
  public static isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!window.PublicKeyCredential &&
      typeof navigator.credentials?.create === 'function'
    );
  }

  /**
   * Registers a new WebAuthn passkey credential.
   */
  public static async registerPasskey(params: RegisterPasskeyParams): Promise<{
    credentialId: string;
    message: string;
  }> {
    if (!this.isSupported()) {
      throw new Error('WebAuthn / Passkeys are not supported in this browser.');
    }

    const apiBase = params.apiBaseUrl || '';

    // 1. Fetch registration challenge options from server
    const challengeRes = await fetch(`${apiBase}/api/v1/auth/webauthn/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'registration',
        userId: params.userId,
        username: params.username,
        displayName: params.displayName,
      }),
    });

    if (!challengeRes.ok) {
      const err = await challengeRes.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to retrieve passkey challenge.');
    }

    const { options } = await challengeRes.json();

    // 2. Format options for navigator.credentials.create
    const creationOptions: CredentialCreationOptions = {
      publicKey: {
        challenge: base64UrlToBuffer(options.challenge),
        rp: options.rp,
        user: {
          id: base64UrlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: options.authenticatorSelection,
        timeout: options.timeout,
        attestation: options.attestation,
      },
    };

    // 3. Trigger native passkey prompt
    const credential = (await navigator.credentials.create(creationOptions)) as PublicKeyCredential | null;
    if (!credential) {
      throw new Error('Passkey creation cancelled or rejected.');
    }

    const rawResponse = credential.response as AuthenticatorAttestationResponse;
    const clientDataJSON = bufferToBase64Url(rawResponse.clientDataJSON);
    const publicKeySpki = rawResponse.getPublicKey ? bufferToBase64Url(rawResponse.getPublicKey()!) : '';

    // 4. Commit credential to server
    const commitRes = await fetch(`${apiBase}/api/v1/auth/webauthn/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: params.userId,
        challenge: options.challenge,
        credentialId: credential.id,
        clientDataJSON,
        publicKeySpki: publicKeySpki || credential.id,
      }),
    });

    if (!commitRes.ok) {
      const err = await commitRes.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to verify and store passkey.');
    }

    const result = await commitRes.json();
    return {
      credentialId: credential.id,
      message: result.message || 'Passkey registered successfully.',
    };
  }

  /**
   * Authenticates using a stored passkey.
   */
  public static async authenticateWithPasskey(params: AuthenticatePasskeyParams): Promise<{
    userId: string;
    sessionToken: string;
  }> {
    if (!this.isSupported()) {
      throw new Error('WebAuthn / Passkeys are not supported in this browser.');
    }

    const apiBase = params.apiBaseUrl || '';

    // 1. Fetch challenge
    const challengeRes = await fetch(`${apiBase}/api/v1/auth/webauthn/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'authentication',
        userId: params.userId,
      }),
    });

    if (!challengeRes.ok) {
      const err = await challengeRes.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to retrieve authentication challenge.');
    }

    const { options } = await challengeRes.json();

    const requestOptions: CredentialRequestOptions = {
      publicKey: {
        challenge: base64UrlToBuffer(options.challenge),
        rpId: options.rpId,
        timeout: options.timeout,
        userVerification: options.userVerification,
        allowCredentials: options.allowCredentials?.map((c: any) => ({
          id: base64UrlToBuffer(c.id),
          type: 'public-key',
        })),
      },
      mediation: params.mediation || 'optional',
    };

    // 2. Trigger native passkey assertion
    const assertion = (await navigator.credentials.get(requestOptions)) as PublicKeyCredential | null;
    if (!assertion) {
      throw new Error('Passkey assertion cancelled.');
    }

    const rawResponse = assertion.response as AuthenticatorAssertionResponse;
    const clientDataJSON = bufferToBase64Url(rawResponse.clientDataJSON);
    const authenticatorData = bufferToBase64Url(rawResponse.authenticatorData);
    const signature = bufferToBase64Url(rawResponse.signature);

    // 3. Verify assertion with server
    const verifyRes = await fetch(`${apiBase}/api/v1/auth/webauthn/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        challenge: options.challenge,
        credentialId: assertion.id,
        clientDataJSON,
        authenticatorData,
        signature,
      }),
    });

    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({}));
      throw new Error(err.message || 'Passkey verification failed.');
    }

    const data = await verifyRes.json();
    return {
      userId: data.userId,
      sessionToken: data.sessionToken,
    };
  }
}
